import React, { useState } from 'react';
import '../../theme/theme.css';
import './css/DetailView.css';

import Attachment from '../Attachment';
import ResizableTable from '../common/ResizableTable';
import RelatedListView from './RelatedListView';
import TaskActivityTimeline from './TaskActivityTimeline';
import { formatFieldValue as formatFieldValueUtil, formatCalculatedValue, normalizeCalculatedDisplayConfig } from '../../utils/formatUtils';

const DEFAULT_RELATED_LIST_ACTIONS = ['new', 'edit', 'clone', 'delete', 'bulk_delete', 'import', 'export'];
//tabs
const getConfiguredActions = (tab) => {
  // Debug: Log the entire tab object to see its structure
  console.log('🔍 getConfiguredActions - Full tab object:', {
    tabName: tab?.tabName,
    tabKeys: tab ? Object.keys(tab) : 'no tab',
    config: tab?.config,
    configKeys: tab?.config ? Object.keys(tab.config) : 'no config',
    configActions: tab?.config?.actions,
    configActionsType: typeof tab?.config?.actions,
    configActionsIsArray: Array.isArray(tab?.config?.actions),
    tabActions: tab?.actions,
    tabActionsType: typeof tab?.actions,
    tabActionsIsArray: Array.isArray(tab?.actions)
  });

  const rawActions = Array.isArray(tab?.config?.actions) && tab.config.actions.length > 0
    ? tab.config.actions
    : Array.isArray(tab?.actions) && tab.actions.length > 0
      ? tab.actions
      : DEFAULT_RELATED_LIST_ACTIONS;

  console.log('🔍 getConfiguredActions - Raw actions selected:', {
    source: Array.isArray(tab?.config?.actions) && tab.config.actions.length > 0
      ? 'tab.config.actions'
      : Array.isArray(tab?.actions) && tab.actions.length > 0
        ? 'tab.actions'
        : 'DEFAULT_RELATED_LIST_ACTIONS',
    rawActions,
    rawActionsType: typeof rawActions,
    rawActionsIsArray: Array.isArray(rawActions),
    rawActionsLength: Array.isArray(rawActions) ? rawActions.length : 'N/A'
  });

  const normalized = rawActions
    .map(action => String(action).toLowerCase())
    .filter(action => DEFAULT_RELATED_LIST_ACTIONS.includes(action));

  console.log('🔍 getConfiguredActions - Normalized result:', {
    normalized,
    hasClone: normalized.includes('clone'),
    rawActionsLength: Array.isArray(rawActions) ? rawActions.length : 0,
    normalizedLength: normalized.length,
    filteredOut: Array.isArray(rawActions)
      ? rawActions.filter(a => !DEFAULT_RELATED_LIST_ACTIONS.includes(String(a).toLowerCase()))
      : []
  });

  let finalActions = normalized.length > 0 ? Array.from(new Set(normalized)) : [...DEFAULT_RELATED_LIST_ACTIONS];

  // Remove 'new' and 'edit' actions for email_log object
  const targetObjectName = tab?.config?.target_object_name;
  if (targetObjectName && targetObjectName.toLowerCase() === 'email_log') {
    finalActions = finalActions.filter(action => action !== 'new' && action !== 'edit');
  }

  return finalActions;
};

const RelatedTabsModal = ({
  tabs,
  activeTab,
  onTabChange,
  onRefresh,
  currentRecordId,
  sourceObjectName,
  onBulkEditRequest = () => { },
  relatedRecordsLoading = false,
  getRelationshipFilterValue,
}) => {
  const [expandedRecords, setExpandedRecords] = useState(new Set());

  const [searchTerms, setSearchTerms] = useState({}); // Store search terms for each tab
  const [showRelatedTabsModal, setShowRelatedTabsModal] = useState(false);
  const [currentModalTab, setCurrentModalTab] = useState(null);
  const [sortBy, setSortBy] = useState(''); // Current sort field
  const [sortOrder, setSortOrder] = useState('asc'); // Current sort order
  const [selectedRows, setSelectedRows] = useState({}); // Store selected rows for each tab
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);

  // Debug: Log tabs data when component receives it
  console.log('RelatedTabsModal received tabs:', tabs);
  if (tabs && tabs.length > 0) {
    tabs.forEach((tab, index) => {
      console.log(`Tab ${index}:`, {
        tabName: tab.tabName,
        hasConfig: !!tab.config,
        configKeys: tab.config ? Object.keys(tab.config) : 'No config',
        target_object_name: tab.config?.target_object_name,
      });
    });
  }

  // Toggle record expansion
  const toggleRecordExpansion = recordId => {
    const newExpanded = new Set(expandedRecords);
    if (newExpanded.has(recordId)) {
      newExpanded.delete(recordId);
    } else {
      newExpanded.add(recordId);
    }
    setExpandedRecords(newExpanded);
  };

  // Handle row selection for ResizableTable
  const handleRowSelectionChange = (tabName, selectedIds) => {
    setSelectedRows(prev => ({
      ...prev,
      [tabName]: selectedIds,
    }));
  };

  // Prepare data for ResizableTable
  const prepareTableData = tab => {
    const filteredRecords = getFilteredAndSortedRecords(tab);
    if (!filteredRecords || filteredRecords.length === 0) return [];

    return filteredRecords.map(record => {
      // Extract the actual ID value
      let recordId;
      if (record.id && typeof record.id === 'object' && record.id.value) {
        recordId = record.id.value;
      } else if (typeof record.id === 'string') {
        recordId = record.id;
      } else {
        // Try to find ID field in the fields array (case-insensitive)
        const idField = record.fields?.find(
          field => field.fieldName?.toLowerCase() === 'id'
        );
        recordId = idField?.value || record.id;
      }

      console.log('Record ID extraction:', {
        originalId: record.id,
        extractedId: recordId,
        recordFields: record.fields?.map(f => ({
          name: f.fieldName,
          value: f.value,
        })),
      });

      const tableRecord = {
        id: recordId,
        objectName: tab.config?.target_object_name || 'object',
        currency: record.currency || null,
        exchange_rate: record.exchange_rate ?? record.exchangeRate ?? null,
      };
      tableRecord.__recordId = recordId;
      // Use record.currency (set by backend even when Currency is not on layout) or
      // fallback to currency from record.fields so amount/price columns show correct symbol
      if (!tableRecord.currency && Array.isArray(record.fields)) {
        const currencyField = record.fields.find(
          field => String(field.fieldName || '').toLowerCase() === 'currency'
        );
        tableRecord.currency = currencyField?.value ?? currencyField?.displayValue ?? null;
      }
      // Same for exchange_rate (ResizableTable uses it for base-currency equivalent + API fallback)
      if (
        (tableRecord.exchange_rate == null || tableRecord.exchange_rate === '') &&
        Array.isArray(record.fields)
      ) {
        const erField = record.fields.find(
          field => String(field.fieldName || '').toLowerCase() === 'exchange_rate'
        );
        tableRecord.exchange_rate =
          erField?.value ?? erField?.displayValue ?? tableRecord.exchange_rate;
      }

      // Add all fields to the record
      record.fields.forEach(field => {
        // Check if this is a file size field and format it
        const isFileSizeField = field.fieldName && (
          field.fieldName.toLowerCase() === 'file_size' ||
          field.fieldName.toLowerCase() === 'filesize' ||
          field.fieldName.toLowerCase() === 'size' ||
          field.fieldName.toLowerCase().endsWith('_size')
        );

        if (isFileSizeField && field.value) {
          // Format file size and update the field's displayValue
          const formattedSize = formatFileSize(field.value);
          field.displayValue = formattedSize;
          // Also update the value to the formatted version for display
          field.formattedValue = formattedSize;
        }

        // Ensure lookup fields have the correct properties for hyperlink rendering
        if (
          field.type === 'lookup' ||
          field.isReference ||
          field.referenceObject
        ) {
          field.isReference = true;
          field.referenceObject = field.referenceObject || 'object';
        }

        // Handle fields ending with 'id' as potential lookup fields
        if (
          field.fieldName.toLowerCase().endsWith('id') &&
          field.displayValue
        ) {
          field.isReference = true;
          field.referenceObject = field.referenceObject || 'object';
        }

        if ((field.type || '').toLowerCase() === 'calculated') {
          const normalizedConfig = normalizeCalculatedDisplayConfig(field.displayConfig);
          field.calculatedReturnType = normalizedConfig.returnType;
          field.calculatedConfig = normalizedConfig;
          field.displayValue = formatCalculatedValue(field.value, field.displayConfig);
        }

        tableRecord[field.fieldName] = field;
      });

      return tableRecord;
    });
  };

  // Prepare columns for ResizableTable
  const prepareTableColumns = tab => {
    const filteredRecords = getFilteredAndSortedRecords(tab);
    if (!filteredRecords || filteredRecords.length === 0) return [];

    const columns = [];

    // Add configured fields only (exclude id by default)
    filteredRecords[0].fields
      .filter(field => field.fieldName?.toLowerCase() !== 'id')
      .forEach(field => {
        columns.push({
          field: field.fieldName,
          label: field.label || field.fieldName,
          sortable: true,
        });
      });

    return columns;
  };

  // Get header actions for ResizableTable
  const getHeaderActions = tab => {
    const isAttachmentTab =
      tab.config?.target_object_name === 'attachment' ||
      tab.tabName?.toLowerCase().includes('attachment');

    const isHistoryObject = tab.config?.target_is_history_object === true;
    const allowedActions = getConfiguredActions(tab);
    const selectedCount = selectedRows[tab.tabName]?.length || 0;

    const actions = [];

    if (
      !isHistoryObject &&
      (allowedActions.includes('new') || allowedActions.includes('edit'))
    ) {
      actions.push({
        label: 'Add / Edit',
        icon: 'fas fa-pen',
        variant: 'outline-primary',
        onClick: () => handleBulkEditAction(tab),
      });
    }

    if (!isHistoryObject && allowedActions.includes('new')) {
      actions.push({
        label: isAttachmentTab ? 'Upload' : 'New',
        icon: isAttachmentTab ? 'fas fa-upload' : 'fas fa-plus',
        variant: 'primary',
        onClick: () => {
          if (isAttachmentTab) {
            setShowAttachmentModal(true);
          } else {
            console.log('New button clicked for tab:', tab);
          }
        },
      });
    }

    if (!isHistoryObject && allowedActions.includes('bulk_delete')) {
      actions.push({
        label: 'Bulk Delete',
        icon: 'fas fa-trash',
        variant: 'danger',
        onClick: () => {
          console.log(
            'Bulk delete selected records:',
            selectedRows[tab.tabName] || []
          );
        },
        disabled: selectedCount === 0,
      });
    }

    if (!isHistoryObject && allowedActions.includes('import')) {
      actions.push({
        label: 'Import',
        icon: 'fas fa-file-import',
        variant: 'outline-primary',
        onClick: () => {
          console.log('Import clicked for tab:', tab);
        },
      });
    }

    if (allowedActions.includes('export')) {
      actions.push({
        label: 'Export',
        icon: 'fas fa-download',
        variant: 'primary',
        onClick: () => {
          console.log(
            'Export selected records:',
            selectedRows[tab.tabName] || []
          );
        },
        disabled: selectedCount === 0,
      });
    }

    return actions;
  };

  const extractRecordId = record => {
    if (!record) return null;
    if (typeof record.id === 'string') {
      return record.id;
    }
    if (record.id && typeof record.id === 'object' && record.id.value) {
      return record.id.value;
    }
    const idField = record.fields?.find(
      field => field.fieldName?.toLowerCase() === 'id'
    );
    return idField?.value || null;
  };

  const handleBulkEditAction = tab => {
    const filteredRecords = getFilteredAndSortedRecords(tab);
    const selectedIds = selectedRows[tab.tabName] || [];

    const selectedRecords = selectedIds.length
      ? filteredRecords.filter(record =>
        selectedIds.includes(extractRecordId(record))
      )
      : [];

    onBulkEditRequest({
      tab,
      selectedRecords,
      allRecords: filteredRecords,
      parentRecordId: currentRecordId,
      parentObjectName: sourceObjectName,
    });
  };

  // Get dynamic actions for ResizableTable
  const getDynamicActions = tab => {
    const isAttachmentTab =
      tab.config?.target_object_name === 'attachment' ||
      tab.tabName?.toLowerCase().includes('attachment');

    const isHistoryObject = tab.config?.target_is_history_object === true;
    const allowedActions = getConfiguredActions(tab);

    console.log('🔍 getDynamicActions for tab:', tab.tabName, {
      allowedActions,
      isAttachmentTab,
      isHistoryObject,
      configActions: tab.config?.actions,
    });

    if (isAttachmentTab) {
      const actions = [
        {
          label: 'View',
          icon: 'fas fa-eye',
          action: 'view',
        },
        {
          label: 'Download',
          icon: 'fas fa-download',
          action: 'download',
        },
      ];

      if (!isHistoryObject && allowedActions.includes('delete')) {
        actions.push({
          label: 'Delete',
          icon: 'fas fa-trash',
          action: 'delete',
        });
      }

      return actions;
    }

    if (isHistoryObject) {
      return [];
    }

    const actions = [
      {
        label: 'View',
        icon: 'fas fa-eye',
        action: 'view',
      },
    ];

    if (allowedActions.includes('edit')) {
      actions.push({
        label: 'Edit',
        icon: 'fas fa-edit',
        action: 'edit',
      });
    }

    if (allowedActions.includes('clone')) {
      console.log('✅ Adding clone action to actions array');
      actions.push({
        label: 'Clone',
        icon: 'fas fa-clone',
        action: 'clone',
      });
    } else {
      console.log('❌ Clone action NOT included. allowedActions:', allowedActions);
    }

    if (allowedActions.includes('delete')) {
      actions.push({
        label: 'Delete',
        icon: 'fas fa-trash',
        action: 'delete',
      });
    }

    console.log('🔍 Final actions array:', actions);
    return actions;
  };

  // Get the primary field for the record (first column)
  const getPrimaryField = record => {
    // Priority order for primary field
    const primaryFieldNames = [
      'name',
      'firstname',
      'title',
      'subject',
      'accountname',
    ];

    for (const fieldName of primaryFieldNames) {
      const field = record.fields.find(f => f.fieldName === fieldName);
      if (field && field.value) {
        return field;
      }
    }

    // Fallback to first field with a value
    const firstField = record.fields.find(f => f.value);
    return firstField || record.fields[0];
  };

  // Format file size helper function
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    // Handle both number and string inputs
    const sizeInBytes = typeof bytes === 'string' ? parseFloat(bytes) : bytes;
    if (isNaN(sizeInBytes) || sizeInBytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(sizeInBytes) / Math.log(k));
    return parseFloat((sizeInBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format field value for display
  const formatFieldValue = field => {
    // Handle file size fields
    const isFileSizeField = field.fieldName && (
      field.fieldName.toLowerCase() === 'file_size' ||
      field.fieldName.toLowerCase() === 'filesize' ||
      field.fieldName.toLowerCase() === 'size' ||
      field.fieldName.toLowerCase().endsWith('_size')
    );

    if (isFileSizeField && field.value) {
      return formatFileSize(field.value);
    }

    // Handle URL fields (need to return JSX)
    if (field.type === 'url' && field.value) {
      return (
        <a href={field.value} target='_blank' rel='noopener noreferrer'>
          {field.value}
        </a>
      );
    }

    if ((field.type || '').toLowerCase() === 'calculated') {
      const normalizedConfig = normalizeCalculatedDisplayConfig(field.displayConfig);
      const returnType = normalizedConfig.returnType;
      const value = field.value;

      if (returnType === 'url' && value) {
        const displayText = formatCalculatedValue(value, field.displayConfig);
        return (
          <a href={String(value)} target='_blank' rel='noopener noreferrer'>
            {displayText || value}
          </a>
        );
      }

      if (returnType === 'image' && value) {
        const rawConfig = normalizedConfig.rawConfig || {};
        const width = rawConfig.width ?? 60;
        const height = rawConfig.height ?? 60;
        return (
          <img
            src={value}
            alt={field.label || 'Calculated image'}
            style={{
              maxWidth: width,
              maxHeight: height,
              objectFit: rawConfig.objectFit ?? 'cover',
              borderRadius: '4px',
              border: '1px solid #ddd'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentNode.innerHTML = '<span style="color: #999; font-size: 11px;">No image</span>';
            }}
          />
        );
      }

      return formatCalculatedValue(value, field.displayConfig);
    }

    // Handle lookup/uuid fields - make them clickable links (need to return JSX)
    if (field.type === 'lookup' || field.type === 'uuid') {
      console.log(`Lookup field ${field.fieldName}:`, {
        type: field.type,
        value: field.value,
        displayValue: field.displayValue,
        referenceObject: field.referenceObject,
      });

      const displayText = field.displayValue || field.value;

      if (field.referenceObject && field.value) {
        return (
          <a
            href={`/view/${field.referenceObject}/${field.value}`}
            target='_blank'
            rel='noopener noreferrer'
            className='lookup-link'
          >
            {displayText}
          </a>
        );
      }

      return displayText;
    }

    // Handle multipicklist fields
    if (field.type === 'multipicklist' && field.value) {
      return field.value.split(';').join(', ');
    }

    // Use centralized formatting utility for all other field types
    // This handles dates, numbers, currency, booleans based on user preferences
    return formatFieldValueUtil(field);
  };

  // Handle navigation to detail page
  const navigateToDetail = (record, tab) => {
    // You can customize this based on your routing structure
    const objectName = tab.config?.target_object_name || 'object';
    window.open(`/view/${objectName}/${record.id}`, '_blank');
  };

  // Handle navigation to list view
  const navigateToListView = tab => {
    const objectName = tab.config?.target_object_name || 'object';
    window.open(`/listview/${objectName}`, '_blank');
  };

  // Handle opening related tabs modal
  const handleShowAllRecords = tab => {
    console.log('Opening related tabs modal for tab:', tab);
    console.log('Tab config:', tab.config);
    console.log('Current record ID:', currentRecordId);

    // Validate that we have a valid object name
    const objectName = tab.config?.target_object_name;
    if (!objectName) {
      console.error('Missing target_object_name in tab config:', tab);
      alert('Error: No target object name found for this related list.');
      return;
    }

    // Store the current tab in window object for the modal to access
    window.currentModalTab = tab;

    setCurrentModalTab(tab);
    setShowRelatedTabsModal(true);
  };

  const refreshRelatedList = () => {
    console.log('$$ refreshRelatedList');
    if (onRefresh) {
      onRefresh();
    }
  };

  // Handle search term changes for each tab
  const handleSearchChange = (tabName, value) => {
    setSearchTerms(prev => ({
      ...prev,
      [tabName]: value,
    }));
  };

  // Handle sorting
  const handleSort = (tabName, fieldName) => {
    if (sortBy === fieldName) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(fieldName);
      setSortOrder('asc');
    }
  };

  // Filter and sort records
  const getFilteredAndSortedRecords = tab => {
    const searchTerm = searchTerms[tab.tabName] || '';
    let filteredRecords = tab.records || [];

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredRecords = filteredRecords.filter(record => {
        return record.fields.some(field => {
          if (!field.value) return false;

          const fieldValue = field.value.toString().toLowerCase();
          const fieldLabel = (
            field.label ||
            field.fieldName ||
            ''
          ).toLowerCase();

          return (
            fieldValue.includes(searchLower) || fieldLabel.includes(searchLower)
          );
        });
      });
    }

    // Apply sorting
    if (sortBy && sortOrder) {
      filteredRecords = [...filteredRecords].sort((a, b) => {
        let aValue, bValue;

        // Handle ID field specially
        if (sortBy === 'id') {
          const aIdField = a.fields?.find(field => field.fieldName === 'id');
          const bIdField = b.fields?.find(field => field.fieldName === 'id');
          aValue = aIdField?.value || '';
          bValue = bIdField?.value || '';
        } else {
          // Handle other fields
          const aField = a.fields?.find(field => field.fieldName === sortBy);
          const bField = b.fields?.find(field => field.fieldName === sortBy);

          // Use displayValue for lookup fields, otherwise use value
          aValue = aField?.displayValue || aField?.value || '';
          bValue = bField?.displayValue || bField?.value || '';
        }

        // Handle date fields
        const aField = a.fields?.find(field => field.fieldName === sortBy);
        const bField = b.fields?.find(field => field.fieldName === sortBy);

        if (
          aField?.type === 'date' ||
          aField?.type === 'datetime' ||
          bField?.type === 'date' ||
          bField?.type === 'datetime'
        ) {
          // Convert to Date objects for comparison
          const aDate = new Date(aValue);
          const bDate = new Date(bValue);

          if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
            aValue = aDate.getTime();
            bValue = bDate.getTime();
          } else {
            // If dates are invalid, fall back to string comparison
            aValue = String(aValue).toLowerCase();
            bValue = String(bValue).toLowerCase();
          }
        } else {
          // Convert to strings for comparison
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();

          // Handle numeric values
          const aNum = parseFloat(aValue);
          const bNum = parseFloat(bValue);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            aValue = aNum;
            bValue = bNum;
          }
        }

        // Sort based on order
        if (sortOrder === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    }

    return filteredRecords;
  };

  // Render a single tab
  const renderTab = tab => {
    const isActive = activeTab === tab.tabName;

    if (!isActive) return null;

    // Check if this is an activity timeline tab
    // Activity timeline is identified by target_object_name === 'activity' and target_field_name === 'related_record_id'
    const isActivityTimeline = tab.config?.target_object_name === 'activity' &&
      tab.config?.target_field_name === 'related_record_id' &&
      tab.config?.source_field_name === 'id';

    // Render Activity Timeline component for activity timeline tabs
    if (isActivityTimeline) {
      if (relatedRecordsLoading) {
        return (
          <div className="tab-content active" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
            Loading activities…
          </div>
        );
      }
      return (
        <div className="tab-content active" style={{ padding: '0' }}>
          <TaskActivityTimeline
            recordId={currentRecordId}
            objectName={sourceObjectName}
            onRefresh={onRefresh}
            records={tab.records} // Pass pre-fetched records from related list query
          />
        </div>
      );
    }

    // Debug: Log the first record to see field structure
    if (tab.records && tab.records.length > 0) {
      console.log(`Related tab "${tab.tabName}" first record:`, tab.records[0]);
      console.log(
        `Field types for "${tab.tabName}":`,
        tab.records[0].fields.map(f => ({
          fieldName: f.fieldName,
          type: f.type,
          value: f.value,
          label: f.label,
        }))
      );

      // Special debug for boolean fields
      const booleanFields = tab.records[0].fields.filter(
        f =>
          f.fieldName.toLowerCase().includes('is_') ||
          f.fieldName.toLowerCase().includes('has_') ||
          f.fieldName.toLowerCase().includes('paid') ||
          f.fieldName.toLowerCase().includes('active') ||
          f.fieldName.toLowerCase().includes('enabled')
      );
      if (booleanFields.length > 0) {
        console.log(`Boolean fields found in "${tab.tabName}":`, booleanFields);
      }
    }

    const headerActions = getHeaderActions(tab);
    const dynamicActions = getDynamicActions(tab);

    // Debug: Log actions being passed to ResizableTable
    console.log('🔍 RelatedTabsModal renderTab - Actions for ResizableTable:', {
      tabName: tab.tabName,
      dynamicActions,
      dynamicActionsLength: dynamicActions.length,
      hasClone: dynamicActions.some(a => a.action === 'clone'),
      shouldShowActions: !tab.config?.target_is_history_object && dynamicActions.length > 0
    });

    const shouldShowActions =
      !tab.config?.target_is_history_object && dynamicActions.length > 0;
    const filteredRecords = getFilteredAndSortedRecords(tab);
    const primaryFieldName =
      filteredRecords && filteredRecords.length > 0
        ? (getPrimaryField(filteredRecords[0])?.fieldName ||
          filteredRecords[0].fields?.[0]?.fieldName ||
          'name')
        : 'name';

    return (
      <div className='related-tab-content'>
        <div className='related-list-container'>
          {/* Content - Always Show ResizableTable */}
          <div className='resizable-table-wrapper'>
            <ResizableTable
              data={prepareTableData(tab)}
              columns={prepareTableColumns(tab)}
              loading={relatedRecordsLoading}
              error={null}
              searchTerm={searchTerms[tab.tabName] || ''}
              onSearchChange={value => handleSearchChange(tab.tabName, value)}
              currentPage={1}
              totalRecords={getFilteredAndSortedRecords(tab).length}
              recordsPerPage={getFilteredAndSortedRecords(tab).length}
              onPageChange={() => { }}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={field => handleSort(tab.tabName, field)}
              showSerialNumber={true}
              showActions={shouldShowActions}
              actions={dynamicActions}
              headerActions={headerActions}
              selectedRows={selectedRows[tab.tabName] || []}
              onRowSelectionChange={selectedIds =>
                handleRowSelectionChange(tab.tabName, selectedIds)
              }
              exportFilename={`${tab.config?.target_object_name || tab.tabName}_records`}
              exportSheetName={tab.tabName}
              className='related-list-view-table'
              tableClassName='table-theme'
              emptyMessage={`No ${tab.tabName.toLowerCase()} found`}
              showSearch={true}
              showPagination={false}
              searchPlaceholder={`Search ${tab.tabName.toLowerCase()}...`}
              objectName={tab.config?.target_object_name}
              sourcePage='relatedlist'
              onRefresh={refreshRelatedList}
              recordId={currentRecordId}
              targetField={tab.config?.target_field_name}
              parentObjectName={sourceObjectName}
              recordLinkField={primaryFieldName}
            />
          </div>

          {/* Show More Button - Only when there are records */}
          {getFilteredAndSortedRecords(tab).length > 0 && (
            <div className='show-more-container'>
              <button
                className='btn btn-outline-primary'
                onClick={() => handleShowAllRecords(tab)}
              >
                <i className='fas fa-list'></i> Show All {tab.tabName}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!tabs || tabs.length === 0) {
    return null;
  }

  return (
    <div className='related-tabs-container'>
      {tabs.map(tab => (
        <div
          key={
            tab.tabName || `tab-${tab.config?.target_object_name || 'unknown'}`
          }
        >
          {renderTab(tab)}
        </div>
      ))}

      {/* Related Tabs Modal */}
      {showRelatedTabsModal &&
        currentModalTab &&
        currentModalTab.config?.target_object_name && (
          <RelatedListView
            show={showRelatedTabsModal}
            onHide={() => {
              setShowRelatedTabsModal(false);
              setCurrentModalTab(null);
              // Clean up window object
              delete window.currentModalTab;
            }}
            objectName={currentModalTab.config.target_object_name}
            parentField={currentModalTab.config?.target_field_name}
            parentId={currentRecordId}
            parentFilterValue={
              typeof getRelationshipFilterValue === 'function'
                ? getRelationshipFilterValue(currentModalTab.config?.source_field_name)
                : undefined
            }
            title={`All ${currentModalTab.tabName}`}
            allowedActions={getConfiguredActions(currentModalTab)}
          />
        )}

      {/* Attachment Upload Modal */}
      {showAttachmentModal && (
        <Attachment
          parentId={currentRecordId}
          onUploadSuccess={uploadedFiles => {
            console.log('Files uploaded successfully:', uploadedFiles);
            // Refresh the related list to show new files
            if (onRefresh) {
              onRefresh();
            }
            setShowAttachmentModal(false);
          }}
          onClose={() => setShowAttachmentModal(false)}
        />
      )}
    </div>
  );
};

export default RelatedTabsModal;
